"use client"

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

// Import translations
import en from '@/locales/en.json'
import th from '@/locales/th.json'

type Locale = 'en' | 'th'
type Translations = typeof en

const translations: Record<Locale, Translations> = { en, th }

interface I18nContextType {
    locale: Locale
    setLocale: (locale: Locale) => void
    t: (key: string) => string
}

const I18nContext = createContext<I18nContextType | null>(null)

export function I18nProvider({ children }: { children: ReactNode }) {
    const [locale, setLocaleState] = useState<Locale>('en')

    // Load locale from localStorage on mount
    useEffect(() => {
        const saved = localStorage.getItem('locale') as Locale | null
        if (saved && (saved === 'en' || saved === 'th')) {
            setLocaleState(saved)
        }
    }, [])

    const setLocale = (newLocale: Locale) => {
        setLocaleState(newLocale)
        localStorage.setItem('locale', newLocale)
    }

    // Translation function with dot notation support
    const t = (key: string): string => {
        const keys = key.split('.')
        let value: any = translations[locale]

        for (const k of keys) {
            value = value?.[k]
            if (value === undefined) {
                console.warn(`Translation missing: ${key}`)
                return key
            }
        }

        return typeof value === 'string' ? value : key
    }

    return (
        <I18nContext.Provider value={{ locale, setLocale, t }}>
            {children}
        </I18nContext.Provider>
    )
}

export function useI18n() {
    const context = useContext(I18nContext)
    if (!context) {
        throw new Error('useI18n must be used within I18nProvider')
    }
    return context
}

// Shorthand hook for just translation function
export function useTranslations() {
    const { t } = useI18n()
    return t
}
